/**
 * tm/portal-ui/contact.tsx — 문의 · 답하기 = 카카오톡 채널 · 문자 (전화 버튼 없음 · 지도사 2026-10-12)
 *  - 카톡: 채널 채팅 주소는 글을 미리 채울 수 없다 → 「업체 · 건 이름」을 복사 · 휴대폰은 먼저 「붙여넣기」 안내 창 → [카카오톡 열기]
 *    (바로 넘어가면 안내 토스트를 못 보고 빈 채팅창 앞에서 멈춤 · 2026-10-12 검수)
 *  - 문자: 휴대폰이면 문자 앱을 본문 채워 열고, PC 면 「휴대폰 문자로 보내 주세요 · 번호 복사」 작은 창(브라우저 대화상자 X)
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, G, FONT, Icon, KakaoMark, Sheet, clamp, isTouch } from './tds';

export interface Contact { kakao_url: string; sms_phone: string }

function copyText(t: string): Promise<boolean> {
  if (!navigator.clipboard || !navigator.clipboard.writeText) return Promise.resolve(false);
  return navigator.clipboard.writeText(t).then(() => true).catch(() => false);
}

export function useContact(contact: Contact | null | undefined, toast: (m: string) => void): {
  canKakao: boolean; canSms: boolean;
  kakao: (context: string, mode?: 'reply' | 'ask') => void; sms: (body: string) => void; sheet: ReactNode;
} {
  const [pcSms, setPcSms] = useState(false);
  const [kakaoCtx, setKakaoCtx] = useState<string | null>(null);   // 카톡 안내 창(휴대폰 · PC 같게)
  const [ask, setAsk] = useState(false);   // 문의(지원사업 묻기 · 2026-10-18) — 「답하기」 대신 「문의하기」 말
  const [copyFail, setCopyFail] = useState(false);
  const kakaoUrl = (contact && contact.kakao_url) || '';
  const phone = (contact && contact.sms_phone) || '';

  const kakao = (context: string, mode: 'reply' | 'ask' = 'reply') => {
    if (!kakaoUrl) return;
    setCopyFail(false); setAsk(mode === 'ask'); setKakaoCtx(context);
  };
  const go = () => { if (isTouch()) window.location.href = kakaoUrl; else window.open(kakaoUrl, '_blank', 'noopener'); };
  // 복사가 안 되면(카톡 안 브라우저 등) 넘어가지 않고 「길게 눌러 복사」로 — 이전 클립보드 글이 붙는 것을 막음
  const openKakao = () => {
    const ctx = `${kakaoCtx || ''}\n${ask ? '문의' : '답'}: `;
    if (copyFail) { setKakaoCtx(null); go(); return; }
    void copyText(ctx).then(okc => { if (okc) { setKakaoCtx(null); go(); } else setCopyFail(true); });
  };
  const sms = (body: string) => {
    if (!phone) return;
    if (isTouch()) {
      const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      window.location.href = `sms:${phone.replace(/-/g, '')}${ios ? '&' : '?'}body=${encodeURIComponent(body)}`;
    } else setPcSms(true);
  };

  const kakaoSheet = kakaoCtx !== null ? (
    <Sheet label={ask ? '카카오톡으로 문의하기' : '카카오톡으로 답하기'} onClose={() => setKakaoCtx(null)} maxWidth={640} z={60}>
        <div style={{ fontSize: 20, fontWeight: 700, color: G.g900 }}>{ask ? '카카오톡으로 문의하기' : '카카오톡으로 답하기'}</div>
        <div style={{ fontSize: 16, color: G.g700, marginTop: 8, lineHeight: 1.6, wordBreak: 'keep-all' }}>{copyFail
          ? <>복사가 안 됐어요. <b>아래 글을 길게 눌러 복사</b>한 뒤 카카오톡을 열어 주세요.</>
          : <>카카오톡이 열리면 입력칸을 <b>길게 눌러 「붙여넣기」</b> 한 뒤, <b>이어서 {ask ? '궁금한 점을' : '답을'} 적어</b> 보내 주세요.</>}</div>
        <div style={{ margin: '14px 0 18px', padding: '12px 14px', borderRadius: 12, background: G.g100, fontSize: 16, color: G.g900, lineHeight: 1.5, wordBreak: 'keep-all', userSelect: 'all', ...clamp(3), whiteSpace: 'pre-wrap' }}>{kakaoCtx}{'\n'}{ask ? '문의' : '답'}: </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: 8 }}>
          <Button kind="gray" h={52} onClick={() => setKakaoCtx(null)}>닫기</Button>
          <Button kind="kakao" h={52} onClick={openKakao}><KakaoMark />{copyFail ? '복사 없이 카카오톡 열기' : '카카오톡 열기'}</Button>
        </div>
    </Sheet>
  ) : null;
  const sheet = pcSms ? (
    <div role="dialog" aria-label="문자 보내기" onClick={() => setPcSms(false)}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, fontFamily: FONT, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 360, maxWidth: '100%', background: '#fff', borderRadius: 20, padding: '24px 20px 20px' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: G.g900 }}>휴대폰 문자로 보내 주세요</div>
        <div style={{ fontSize: 15, color: G.g700, marginTop: 6, lineHeight: 1.5 }}>PC 에서는 문자 앱이 열리지 않아요. 아래 번호로 보내 주시면 임지도사가 확인해요.</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: G.g900, margin: '16px 0', fontVariantNumeric: 'tabular-nums', userSelect: 'all' }}>{phone}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Button kind="gray" h={48} onClick={() => setPcSms(false)}>닫기</Button>
          <Button kind="fill" h={48} onClick={() => { void copyText(phone).then(okc => { toast(okc ? '번호를 복사했어요' : '번호를 길게 눌러 복사해 주세요'); setPcSms(false); }); }}>번호 복사</Button>
        </div>
      </div>
    </div>
  ) : kakaoSheet;

  return { canKakao: !!kakaoUrl, canSms: !!phone, kakao, sms, sheet };
}

/** 답하기 두 칸 — 카카오톡(노랑) · 문자(회색 채움) */
/** onUse — 눌렀다는 것을 남김(서버 「답 보냈어요」 + 이 기기) */
export function AskButtons({ c, context, smsBody, h = 48, onUse, ask }: { c: ReturnType<typeof useContact>; context: string; smsBody: string; h?: number; onUse?: (via: 'kakao' | 'sms') => void; ask?: boolean }) {
  if (!c.canKakao && !c.canSms) return null;
  const both = c.canKakao && c.canSms;
  const verb = ask ? '문의' : '답하기';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: both ? 'minmax(0,1fr) minmax(0,1fr)' : '1fr', gap: 8 }}>   {/* 두 칸 같은 폭(글자 길이 따라 168/150 으로 갈리던 것) */}
      {c.canKakao && <Button kind="kakao" h={h} onClick={() => { if (onUse) onUse('kakao'); c.kakao(context, ask ? 'ask' : 'reply'); }}><KakaoMark />카카오톡 {verb}</Button>}
      {c.canSms && <Button kind="gray" h={h} onClick={() => { if (onUse) onUse('sms'); c.sms(smsBody); }}><Icon name="sms" size={18} color={G.g800} />문자 {verb}</Button>}
    </div>
  );
}

/** 맨 아래 작은 안내 한 줄 — 맨 위 연락처 X(문의가 몰림 · 지도사 2026-10-12) */
export function AskFooter({ c, context, pc }: { c: ReturnType<typeof useContact>; context: string; pc?: boolean }) {
  if (!c.canKakao && !c.canSms) return null;
  const lk = { display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 44, padding: '0 10px', fontSize: 15, fontWeight: 600, color: G.g800, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT } as const;
  return (
    <div style={{ padding: pc ? '8px 0 0' : '16px 24px 28px', textAlign: 'center', fontSize: 15, lineHeight: 1.6, color: G.g700, wordBreak: 'keep-all' }}>
      궁금하신 점은 남겨 주시면 답해 드려요
      <div style={{ marginTop: 2, display: 'flex', justifyContent: 'center', gap: 8 }}>
        {c.canKakao && <button type="button" className="tds-btn" style={lk} onClick={() => c.kakao(context, 'ask')}><KakaoMark size={16} />카카오톡 문의</button>}
        {c.canSms && <button type="button" className="tds-btn" style={lk} onClick={() => c.sms(`[${context}] `)}><Icon name="sms" size={16} color={G.g800} />문자 문의</button>}
      </div>
    </div>
  );
}
